import { LocalizationProvider as DatePickerLocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDateFns } from "@mui/x-date-pickers-pro/AdapterDateFnsV3";
import { LoadingAnimated } from "@pmag/icons";
import { Locale } from "@src/context/attributeConfig/AttributeConfigContext";
import { useRemoteTranslations } from "@src/utils/localization/useRemoteTranslations";
import type { Locale as DateFnsLocale } from "date-fns";
import { deAT, enUS, es, fr, it, pt, sv } from "date-fns/locale";
import * as React from "react";
import { IntlProvider } from "react-intl";

import { LoadingContainer } from "./LocalizationProvider.styles";

interface LocalizationProviderProps extends React.PropsWithChildren {
    language: Locale;
}

const localeMap = new Map<Locale, DateFnsLocale>([
    [Locale.DE, deAT],
    [Locale.EN, enUS],
    [Locale.ES_ES, es],
    [Locale.FR, fr],
    [Locale.IT, it],
    [Locale.PT, pt],
    [Locale.SV_SE, sv],
]);

export const LocalizationProvider: React.FunctionComponent<LocalizationProviderProps> = ({ language, children }) => {
    const { loading, messages } = useRemoteTranslations(language);

    const renderChildren = messages != null || !loading;

    const adapterLocale = React.useMemo(() => {
        return localeMap.get(language);
    }, [language]);

    return (
        <IntlProvider locale={language} messages={messages}>
            <DatePickerLocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={adapterLocale}>
                {renderChildren && children}

                {loading && (
                    <LoadingContainer>
                        <LoadingAnimated />
                    </LoadingContainer>
                )}
            </DatePickerLocalizationProvider>
        </IntlProvider>
    );
};
